import { ComparisonLineChart } from '@practium/features/dashboard';
import type { CustomPanelControls, SectionTab } from '@practium/ui';
import {
	ActionButton,
	DetailModal,
	EmptyState,
	FilterChip,
	ListPageChartCard,
	ListPageCharts,
	ListPageLayout,
	Pagination,
	PeriodPanel,
	PromotionCard,
	SectionCard,
	SkeletonBox,
} from '@practium/ui';
import { useGetRequestProductsReport } from '@practium/features/api';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponseTimeChart } from '../components/Charts/ResponseTimeChart/ResponseTimeChart';
import { ResponseTimeDailyChart } from '../components/Charts/ResponseTimeDailyChart/ResponseTimeDailyChart';
import { PendingRequestsMonthlyChart } from '../components/Charts/PendingRequestsMonthlyChart/PendingRequestsMonthlyChart';
import { UpdateStatusModal } from '../components/Modals/UpdateStatusModal/UpdateStatusModal';
import { ProductRequestsTable } from '../components/Tables/ProductRequestsTable/ProductRequestsTable';
import { useProductRequestsPage } from '../hooks/useProductRequestsPage';
import type { ProductRequestsTab } from '../types/productRequest.types';

export const ProductRequestsPage = () => {
	const navigate = useNavigate();
	const [isReportOpen, setIsReportOpen] = useState(false);
	const { data: report, isPending: isReportLoading, isError: isReportError } = useGetRequestProductsReport(isReportOpen);
	const {
		filteredRequests,
		stats,
		isTableLoading,
		isTableFetching,
		isTableError,
		refetchTable,
		isStatsLoading,
		isStatsFetching,
		isStatsError,
		refetchStats,
		activeTab,
		setActiveTab,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort,
		filterValues,
		setFilterValues,
		filterGroups,
		customDateFrom,
		setCustomDateFrom,
		customDateTo,
		setCustomDateTo,
		page,
		setPage,
		totalPages,
		selectedRequest,
		setSelectedRequest,
		handleUpdateStatus
	} = useProductRequestsPage();

	const renderPeriodPanel = useCallback(
		({ close, activate }: CustomPanelControls) => (
			<PeriodPanel
				requireEndDate
				dateFrom={customDateFrom}
				dateTo={customDateTo}
				onChange={(from, to) => {
					setCustomDateFrom(from);
					setCustomDateTo(to);
					activate();
				}}
				onClose={close}
			/>
		),
		[customDateFrom, customDateTo, setCustomDateFrom, setCustomDateTo]
	);

	const tableContent = isTableError ? (
		<div role="alert">
			<EmptyState
				title="Não foi possível carregar as solicitações"
				description="Tente novamente para carregar os dados."
			/>
			<ActionButton text="Tentar novamente" onClick={() => void refetchTable()} />
		</div>
	) : (
		<ProductRequestsTable requests={filteredRequests} onRowClick={setSelectedRequest} />
	);
	const statsError = (
		<div role="alert">
			<EmptyState
				title="Não foi possível carregar as estatísticas"
				description="Tente novamente para carregar os dados."
			/>
			<ActionButton text="Tentar novamente" onClick={() => void refetchStats()} />
		</div>
	);
	const search =
		activeTab == 'pending'
			? 'pendentes'
			: activeTab == 'approved'
				? 'aceitos'
				: activeTab == 'rejected'
					? 'rejeitados'
					: '';
	const tabs: SectionTab<ProductRequestsTab>[] = [
		{
			key: 'pending',
			label: 'Pendentes',
			content: tableContent,
		},
		{
			key: 'approved',
			label: 'Aceitos',
			content: tableContent,
		},
		{
			key: 'rejected',
			label: 'Rejeitados',
			content: tableContent,
		},
		{
			key: 'all',
			label: 'Todos',
			content: tableContent,
		},
	];
	const formatAverage = (minutes: number | null) => minutes === null
		? 'Sem respostas no período'
		: `${minutes.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} min`;
	const reportSections = report ? [
		{
			title: 'Solicitações por ano',
			fields: [{
				label: 'Total',
				value: <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{report.requestsByYear.length
					? report.requestsByYear.map(item => `${item.year}: ${item.count}`).join('\n')
					: 'Nenhuma solicitação registrada.'}</pre>,
				span: 'full' as const,
			}],
		},
		{
			title: 'Tempo de resposta — últimos 15 dias',
			fields: [
				{ label: 'Média', value: formatAverage(report.responseTime.averageMinutes) },
				{ label: 'Solicitações respondidas', value: report.responseTime.count.toLocaleString('pt-BR') },
				{
					label: 'Detalhamento diário',
					value: <ResponseTimeDailyChart days={report.responseTime.days} />,
					span: 'full' as const,
				},
			],
		},
		{
			title: `Solicitações pendentes por mês — ${report.pendingByMonth.year}`,
			fields: [{
				label: 'Meses',
				value: <PendingRequestsMonthlyChart year={report.pendingByMonth.year} months={report.pendingByMonth.months} />,
				span: 'full' as const,
			}],
		},
	] : [{
		title: 'Relatório',
		fields: [{
			label: 'Status',
			value: isReportLoading ? 'Carregando relatório…' : isReportError ? 'Não foi possível carregar o relatório.' : 'Nenhum dado disponível.',
			span: 'full' as const,
		}],
	}];

	return (
		<>
			<ListPageLayout
				sidebarWidth="27rem"
				sidebarW1500="22rem"
				sidebarW1300="19rem"
				sidebarW1200="17rem"
				sidebar={
					<>
						<PromotionCard
							title="Configure os grupos de permissão"
							subtitle="Defina quais grupos podem criar produtos no Practium"
							buttonText="Configurar"
							onButtonClick={() => {
								navigate('/sistema/novo?type=permissions');
							}}
						/>
						<ListPageCharts>
							<ListPageChartCard isFetching={isStatsFetching && !isStatsLoading && !isStatsError}>
								{isStatsError ? statsError : <ComparisonLineChart
									isLoading={isStatsLoading}
									title="Solicitações na Plataforma"
									mainValue={stats?.line.subtitle}
									trendValue={stats ? `${stats.line.growthPercentage.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%` : undefined}
									trendLabel="vs semana anterior"
									trendDirection={(stats?.line.growthPercentage ?? 0) < 0 ? 'down' : 'up'}
									footerText={stats ? `Solicitações de ${stats.line.period}` : undefined}
									buttonText="Ver Relatório"
									onButtonClick={() => setIsReportOpen(true)}
									data={stats?.line.data ?? []}
									xAxisKey="label"
									line1Key="current"
									line1Label="Semana atual"
									line2Key="previous"
									line2Label="Semana anterior"
									tooltipTitle="Solicitações"
								/>}
							</ListPageChartCard>
							<ListPageChartCard isFetching={isStatsFetching && !isStatsLoading && !isStatsError}>
								{isStatsError ? statsError : isStatsLoading ? (
									<div role="status" aria-label="Carregando estatísticas">
										<SkeletonBox width="100%" height="16rem" />
									</div>
								) : <ResponseTimeChart data={stats?.responseTime} />}
							</ListPageChartCard>
						</ListPageCharts>
					</>
				}
			>
				<SectionCard
					title="Solicitações de Produtos"
					subtitle="Gerencie os produtos solicitados pelos usuários para serem cadastrados no sistema"
					headerAction={<ActionButton text="Ver Relatório" onClick={() => setIsReportOpen(true)} />}
					tabs={tabs}
					isLoading={isTableLoading && !isTableError}
					isFetching={isTableFetching && !isTableError}
					loadingLabel="Carregando solicitações"
					activeTab={activeTab}
					onTabChange={setActiveTab}
					search={true}
					searchQuery={searchQuery}
					onSearch={setSearchQuery}
					searchPlaceholder={`Buscar por produtos ${search}...`}
					sortAsc={sortAsc}
					onToggleSort={toggleSort}
					filterSlot={
						<FilterChip
							values={filterValues}
							onChange={setFilterValues}
							groups={filterGroups}
							placeholder="Filtros"
							customPanels={{ 'periodo:custom': renderPeriodPanel }}
						/>
					}
					footer={!isTableLoading && !isTableError ? (
						<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
					) : undefined}
				/>
			</ListPageLayout>

			{selectedRequest && (
				<UpdateStatusModal
					request={selectedRequest}
					onClose={() => setSelectedRequest(null)}
					onSave={handleUpdateStatus}
				/>
			)}
			{isReportOpen && (
				<DetailModal
					title="Relatório de Solicitações de Produtos"
					sections={reportSections}
					footer={<ActionButton text="Fechar" onClick={() => setIsReportOpen(false)} />}
					onClose={() => setIsReportOpen(false)}
				/>
			)}
		</>
	);
};
